/** OCI summary-first API used by the award browser and explicit detail actions. */
import type { NodeApiClient } from '@/app-shell/services'

import type {
  OciAnalysisStatus,
  OciDocument,
  OciDocumentReview,
  OciRecordSummary,
} from './ociDashboardModel'

export interface OciDashboardResponse {
  records: OciRecordSummary[]
  documentsWithFindings: number
  findings: number
}

interface OciCsvExportJobResponse {
  jobId: string
  status: string
}

interface OciCsvExportResponse {
  url: string
  filename: string
}

export const ociDashboardRecordsQueryKey = (orgId: string, workspaceId: string) =>
  ['oci-dashboard-records', orgId, workspaceId] as const

interface OciAttachmentResponse extends OciDocument {
  analysis: OciAnalysisStatus
}

const dashboardPath = (orgId: string, workspaceId: string) => (
  `/org/${orgId}/ws/${workspaceId}/contract-review/oci-dashboard`
)

function context(agentId: string, datasetKey: string): URLSearchParams {
  return new URLSearchParams({ agent_id: agentId, dataset_key: datasetKey })
}

export async function fetchOciDashboard(
  apiClient: NodeApiClient,
  orgId: string,
  workspaceId: string,
  agentId: string,
  datasetKey: string,
): Promise<OciDashboardResponse> {
  const response = await apiClient.get<OciDashboardResponse>(
    `${dashboardPath(orgId, workspaceId)}?${context(agentId, datasetKey)}`,
  )
  return response.data
}

export async function fetchOciAttachments(
  apiClient: NodeApiClient,
  orgId: string,
  workspaceId: string,
  agentId: string,
  datasetKey: string,
  recordKeys: string[],
): Promise<OciDocumentReview[]> {
  const params = context(agentId, datasetKey)
  for (const key of recordKeys) params.append('record_key', key)
  const response = await apiClient.get<{ attachments: OciAttachmentResponse[] }>(
    `${dashboardPath(orgId, workspaceId)}/attachments?${params}`,
  )
  return response.data.attachments.map(({ analysis, ...document }) => ({
    document,
    analysis,
  }))
}

export async function startOciCsvExport(
  apiClient: NodeApiClient,
  orgId: string,
  workspaceId: string,
  agentId: string,
  datasetKey: string,
  recordKeys: string[],
): Promise<OciCsvExportJobResponse> {
  const response = await apiClient.post<OciCsvExportJobResponse>(
    `${dashboardPath(orgId, workspaceId)}/exports`,
    { agentId, datasetKey, recordKeys },
  )
  return response.data
}

export async function fetchOciCsvExport(
  apiClient: NodeApiClient,
  orgId: string,
  workspaceId: string,
  jobId: string,
): Promise<OciCsvExportResponse> {
  const response = await apiClient.get<OciCsvExportResponse>(
    `${dashboardPath(orgId, workspaceId)}/exports/${encodeURIComponent(jobId)}`,
  )
  return response.data
}
